/* ==========================================================================
   API client.

   Session cookies, no tokens. Auth is the httpOnly `asset_sid` cookie the server
   sets — there is deliberately nothing here that reads or writes a token, and
   nothing in localStorage. If you find yourself adding either, check SETUP.md §4.

   Same origin throughout: Parcel proxies /api to Fastify in dev (.proxyrc),
   and Fastify serves the built SPA in production. So no CORS, and no base URL
   to configure.
   ========================================================================== */

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

async function request(path, { method = "GET", body } = {}) {
  let res;
  try {
    res = await fetch(path, {
      method,
      // Explicit, though it is the default for same-origin: the session cookie
      // must ride along on every call.
      credentials: "same-origin",
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (err) {
    // fetch only rejects on a transport failure — the server being down, or the
    // Parcel proxy not reaching it. Worth saying plainly, because the browser's
    // own message ("Failed to fetch") sends people looking in the wrong place.
    throw new ApiError(
      "Could not reach the server. Is it running on :8080? (cd server && npm run dev)",
      0,
      null,
    );
  }

  if (res.status === 204) return null;

  const text = await res.text();
  let payload = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    const message =
      (payload && typeof payload === "object" && (payload.error || payload.message)) ||
      (typeof payload === "string" && payload) ||
      `Request failed (HTTP ${res.status})`;
    throw new ApiError(message, res.status, payload);
  }

  return payload;
}

const qs = (params) => {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined && v !== null && v !== "") q.set(k, v);
  }
  const s = q.toString();
  return s ? `?${s}` : "";
};

/* ── Auth ────────────────────────────────────────────────────────────────── */

export const auth = {
  // What the sign-in page should offer: { sso, password, forced }. Public.
  options: () => request("/api/auth/options"),

  // Current user, or null when not signed in. The 401 is an expected answer
  // here, not an error — every caller would otherwise have to catch it.
  me: async () => {
    try {
      return await request("/api/me");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },

  login: (email, password) =>
    request("/api/auth/login", { method: "POST", body: { email, password } }),

  logout: () => request("/api/auth/logout", { method: "POST" }),

  // NAVIGATE to this, do not fetch it: the response is a 302 to
  // login.microsoftonline.com, which fetch cannot usefully follow.
  //   window.location.href = auth.ssoStartUrl();
  ssoStartUrl: () => "/api/auth/sso/start",
};

/* ── Assets, people, overview ────────────────────────────────────────────── */
/* These need server/routes.js, which is not written yet (SETUP.md §9). The
   client side is here so AssetInventory.jsx has something to call during the
   task-4 rewire. */

export const api = {
  reference: () => request("/api/reference"),

  assets: (filters) => request(`/api/assets${qs(filters)}`),

  // What a QR scan resolves to: the asset, its holder, and its history.
  asset: (tag) => request(`/api/assets/${encodeURIComponent(tag)}`),

  createAsset: (fields) => request("/api/assets", { method: "POST", body: fields }),

  // Bulk import. { text, defaultCategory, dryRun } — dryRun returns the same
  // per-row plan without writing. See server/import.js.
  importAssets: (payload) => request("/api/assets/import", { method: "POST", body: payload }),

  // The only way to change an asset is to record something that happened to it.
  // kind is one of: assign (holderId), return, transfer (department),
  // status (status), audit. There is no PATCH /api/assets/:tag, by design.
  recordEvent: (tag, payload) =>
    request(`/api/assets/${encodeURIComponent(tag)}/events`, { method: "POST", body: payload }),

  people: () => request("/api/people"),
  createPerson: (fields) => request("/api/people", { method: "POST", body: fields }),
  updatePerson: (id, fields) =>
    request(`/api/people/${encodeURIComponent(id)}`, { method: "PATCH", body: fields }),

  overview: () => request("/api/overview"),

  // Directory sync (Microsoft Graph, app-only). `{ dryRun: true }` returns the
  // same summary without writing — see server/graph.js.
  syncOrg: (opts) => request("/api/org/sync", { method: "POST", body: opts ?? {} }),
  orgStatus: () => request("/api/org/status"),

  /* ── Admin ───────────────────────────────────────────────────────────── */

  ssoSettings: () => request("/api/settings/sso"),
  saveSso: (fields) => request("/api/settings/sso", { method: "PUT", body: fields }),
  testSso: (fields) => request("/api/settings/sso/test", { method: "POST", body: fields }),

  users: () => request("/api/users"),
  setUserRole: (id, role) =>
    request(`/api/users/${encodeURIComponent(id)}/role`, { method: "PATCH", body: { role } }),
};

/* ── Permissions ─────────────────────────────────────────────────────────── */

// Mirrors server/session.js. This copy decides which buttons are visible; the
// server decides what actually happens. Never the other way round — a technician
// who calls the register endpoint directly still gets a 403.
export const PERMISSIONS = {
  admin: ["read", "assign", "status", "register", "staff", "settings", "users"],
  technician: ["read", "assign", "status"],
  viewer: ["read"],
};

export function can(user, permission) {
  // employees.app_role may be NULL — someone who exists so equipment can be
  // issued to them but cannot sign in. Must not throw.
  const role = user?.role;
  return !!role && (PERMISSIONS[role] ?? []).includes(permission);
}

/* ── Display labels ──────────────────────────────────────────────────────── */

// The database stores lowercase snake_case and that is authoritative — these map
// to what a person reads. Do not "fix" the enum values to match a label.

export const STATUS_LABELS = {
  available: "Available",
  assigned: "Assigned",
  in_repair: "In repair",
  awaiting_return: "Awaiting return",
  retired: "Retired",
  lost: "Lost",
};

export const STAFF_STATUS_LABELS = {
  active: "Active",
  on_leave: "On leave",
  notice: "Notice period",
  offboarded: "Offboarded",
};

export const ROLE_LABELS = {
  admin: "Administrator",
  technician: "Technician",
  viewer: "Read only",
};

export const EVENT_LABELS = {
  registered: "Registered",
  assigned: "Assigned",
  returned: "Returned",
  transferred: "Transferred",
  status_changed: "Status changed",
  audit_scan: "Audit scan",
  recovery_flagged: "Flagged for recovery",
};

export const statusLabel = (s) => STATUS_LABELS[s] ?? s ?? "—";
export const staffStatusLabel = (s) => STAFF_STATUS_LABELS[s] ?? s ?? "—";
export const roleLabel = (r) => ROLE_LABELS[r] ?? "No sign-in access";
export const eventLabel = (e) => EVENT_LABELS[e] ?? e ?? "—";
